import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { AdminQuestionnaireTemplateViewModel } from '../api';

export function DeleteQuestionnaireTemplateConfirmDialog({
  row,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly row: AdminQuestionnaireTemplateViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (row: AdminQuestionnaireTemplateViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.questionnaires.templates.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.admin.questionnaires.templates.delete.message', { label: row?.label ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.questionnaires.templates.delete.errors.submit', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.admin.questionnaires.templates.form.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (row !== null) onConfirm(row);
            }}
          >
            {t('routes.admin.questionnaires.templates.delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
